<template>
    <div class="lni-c-isolated-workers-complaint lni-c-box--content lni-u-mhauto lni-u-mv3">
        <h1 class="lni-u-heading--1">
            Confirmation
        </h1>
        <h2 class="lni-u-heading--2 lni-u-mt3 lni-u-mb2">
            We've received your complaint
        </h2>
        <a
            href="#"
            @click="print"
        >
            <span
                class="lnicon--print lni-u-mr1"
                aria-hidden="true"
            /> Get your PDF
        </a>
        <div class="lni-u-mt2">
            <span class="lni-u-heading--5 lni-u-text--all-caps">
                Complaint Number: {{ complaintNumber }}
            </span>
            <p>{{ text.confirmationText }}</p>
        </div>
        <div class="lni-u-mt3 lni-u-pt3 lni-u-bt lni-u-b--subtle">
            <h3 class="lni-u-heading--3">
                {{ text.allegationsHeading }}
            </h3>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.allegationsChecklistLabel }}:
                </span>
                <ul class="lni-u-reading-width--max lni-u-pl3">
                    <li
                        v-for="allegation in allegations"
                        :key="allegation"
                    >
                        {{ allegation }}
                    </li>
                </ul>
            </div>
            <div class="lni-u-mt2">
                <span class="lni-u-type--bold">
                    {{ text.additionalQuestionsDescribeTextLabel }}:
                </span>
                <p class="lni-u-reading-width--max">
                    {{ getText(complaint.description) }}
                </p>
            </div>
        </div>
        <div class="lni-u-mt3">
            <h3 class="lni-u-heading--3">
                {{ text.complaintSubmissionHeading }}
            </h3>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionRoleLabel }}:
                </span>
                {{ getText(role) }}
            </div>
            <template v-if="!isAnonymous">
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionFirstNameLabel }}:
                </span>
                {{ getText(complainant.firstName) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionLastNameLabel }}:
                </span>
                {{ getText(complainant.lastName) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionPhoneLabel }}:
                </span>
                {{ getText(complainant.phone) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionEmailLabel }}:
                </span>
                {{ getText(complainant.email) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.complaintSubmissionLangLabel }}:
                </span>
                {{ getText(preferredLanguage) }}
                <br>
                <div class="lni-u-mt1">
                    <span class="lni-u-type--bold">
                        {{ text.complaintSubmissionAddressLabel }}:
                    </span>
                    <address v-if="complainant.address1.length">
                        {{ complainant.address1 }}
                        <span v-if="complainant.address2.length">
                            <br>
                            {{ complainant.address2 }}
                        </span>
                        <br>
                        {{ complainant.city }}, {{ complainant.state }} {{ complainant.zipCode }}
                        <span v-if="complainant.county.length">
                            <br>
                            {{ getCountyText(complainant.county) }}
                        </span>
                    </address>
                </div>
            </template>
        </div>
        <div class="lni-u-mt3">
            <h3 class="lni-u-heading--3">
                {{ text.employerHeading }}
            </h3>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.employerNameLabel }}:
                </span>
                {{ getText(respondent.name) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.employerPhoneLabel }}:
                </span>
                {{ getText(respondent.phone) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.employerEmailLabel }}:
                </span>
                {{ getText(respondent.email) }}
            </div>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.employerAddressLabel }}:
                </span>
                <address v-if="respondent.address1.length">
                    {{ respondent.address1 }}
                    <span v-if="respondent.address2.length">
                        <br>
                        {{ respondent.address2 }}
                    </span>
                    <br>
                    {{ respondent.city }}, {{ respondent.state }} {{ respondent.zipCode }}
                    <span v-if="respondent.county.length">
                        <br>
                        {{ getCountyText(respondent.county) }}
                    </span>
                </address>
            </div>
            <div class="lni-u-mt1">
                <h4 class="lni-u-heading--4">
                    {{ text.contactHeading }}
                </h4>
                <span class="lni-u-type--bold">
                    {{ text.contactFirstNameLabel }}:
                </span>
                {{ getText(respondent.contactFirstName) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.contactLastNameLabel }}:
                </span>
                {{ getText(respondent.contactLastName) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.contactPhoneLabel }}:
                </span>
                {{ getText(respondent.contactPhone) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.contactEmailLabel }}:
                </span>
                {{ getText(respondent.contactEmail) }}
                <br>
            </div>
        </div>
        <div class="lni-u-mt3">
            <h3 class="lni-u-heading--3">
                {{ text.additionalQuestionsHeading }}
            </h3>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.additionalQuestionsDateStartWorkLabel }}:
                </span>
                {{ getText(additionalQuestions.dateStartWork) }}
                <br>
                <span class="lni-u-type--bold">
                    {{ text.additionalQuestionsDateEndWorkLabel }}:
                </span>
                {{ getText(additionalQuestions.dateEndWork) }}
                <div class="lni-u-mt1">
                    <h4 class="lni-u-heading--4">
                        {{ text.additionalQuestionsDateRangeComplaintLabel }}
                    </h4>
                    <span class="lni-u-type--bold">
                        {{ text.additionalQuestionsDateStartComplaintLabel }}:
                    </span>
                    {{ getText(additionalQuestions.dateStartComplaint) }}
                    <br>
                    <span class="lni-u-type--bold">
                        {{ text.additionalQuestionsDateEndComplaintLabel }}:
                    </span>
                    {{ getText(additionalQuestions.dateEndComplaint) }}
                </div>
            </div>
        </div>
        <div class="lni-u-mt3">
            <h3 class="lni-u-heading--3">
                {{ text.docUploadHeading }}
            </h3>
            <div v-show="documents.length === 0">
                None
            </div>
            <lni-file-uploads
                id="confirmation_file-uploads"
                class="lni-u-mb2"
                :uploadedFiles="documents"
            />
        </div>
        <div class="lni-u-pt3 lni-u-bt lni-u-b--subtle">
            <div class="lni-u-reading-width--max">
                <lni-html-element
                    id="confirmation_next-steps"
                    :htmlString="text.confirmationNextSteps"
                />
                <lni-html-element
                    id="confirmation_next-options"
                    class="lni-u-mt3"
                    :htmlString="text.confirmationNextOptions"
                />
                <lni-html-element
                    id="confirmation_contact"
                    class="lni-u-mt3"
                    :htmlString="text.confirmationContact"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ConfirmationPage',
    data() {
        return {
            placeholder: '---',
        };
    },
    computed: {
        additionalQuestions() {
            return this.$store.state.appState?.additionalQuestions;
        },
        allegations() {
            const result = [];
            const options = this.$store.state.appState?.allegationsChecklistOptions
            const values = this.$store.state.appState?.allegations;

            if (values.length) {
                for (const value of values) {
                    for (const option of options) {
                        if (value === option.value) {
                            result.push(option.text);
                        }
                    }
                }
            }

            return result;
        },
        complainant() {
            return this.$store.state.appState?.complainant;
        },
        complaint() {
            return this.$store.state.appState?.complaint;
        },
        complaintNumber() {
            return this.$store.state.appState?.complaintNumber;
        },
        counties() {
            return this.$store.state.appState?.counties;
        },
        documents() {
            return this.$store.state.appState?.documents;
        },
        isAnonymous() {
            return this.$store.getters['appState/isAnonymous'];
        },
        preferredLanguage() {
            return this.$store.state.appState?.complaintSubmissionLangOptions.find(option =>
                option.value === this.complainant.language).text;
        },
        respondent() {
            return this.$store.state.appState?.respondent;
        },
        role() {
            return this.$store.state.appState?.complaintSubmissionRoleOptions.find(option =>
                option.value === this.complainant.role).text;
        },
        text() {
            return this.$store.state.appState?.text;
        },
    },
    methods: {
        getCountyText(value) {
            const result = this.counties.find(opt => opt.value === value)?.text;
            return result ? `${result} County` : '';
        },
        getText(value) {
            return value && value.length ? value : this.placeholder;
        },
        print() {
            window.print();
        },
    },
};
</script>

<style lang="scss">
@import '@gov.wa.lni/framework.one-lni/source/styles/settings';

.lni-c-isolated-workers-complaint {
    .lni-u-heading--5 {
        color: $text--alternate;
    }
}
</style>
